import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { setToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Stepper, Card, Field, NavRow } from "@/components/OnboardKit";
import { ArrowRight, ShieldCheck, MapPin } from "lucide-react";
import { toast } from "sonner";
import OnboardBackButton from "@/components/OnboardBackButton";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;
const STEPS = ["Studio Details", "Owner Account", "Verified"];

/** L4 Studio · profile creation form.
 *
 *  Reads the pre-picked facility types + trade affiliations from
 *  route state (set by /onboard/studio, the pre-profile picker).
 *  Bounces back to that picker if the user lands here directly.
 */
export default function OnboardStudio({ kind = "studio" }) {
  const nav = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  // Pull picks from the pre-profile screen. Bounce if missing.
  const studioFacilityTypes = location.state?.studioFacilityTypes || [];
  const sectionAffiliations = location.state?.sectionAffiliations || [];
  useEffect(() => {
    if (studioFacilityTypes.length === 0) {
      nav("/onboard/studio", { replace: true });
    }
  }, []);   // eslint-disable-line

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [legal, setLegal] = useState("");
  const [brand, setBrand] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [mapsUrl, setMapsUrl] = useState("");
  const [gstin, setGstin] = useState("");
  const [pan, setPan] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerPwd, setOwnerPwd] = useState("");

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/onboard/org`, {
        org_type: kind,
        legal_name: legal,
        brand_name: brand || legal,
        registered_address: address,
        registered_city: city,
        registered_state: state,
        google_maps_url: mapsUrl,
        gstin, pan,
        owner_name: ownerName,
        owner_email: ownerEmail,
        owner_phone: ownerPhone,
        owner_password: ownerPwd,
        studio_facility_types: studioFacilityTypes,
        section_affiliations: sectionAffiliations,
      });
      await setToken(data.access_token);
      await refresh();
      setStep(2);
      toast.success(`Welcome — ${data.organization.brand_name} is verified`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="onboard-studio-page">
      <OnboardBackButton fallback="/onboard/studio" label="Change Facility Type" />
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
        L4 · Sign up · Step 2 of 2
      </div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">
        Set Up Your Studio
      </h1>

      {/* Recap of what user already picked */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5"
           data-testid="onboard-studio-recap">
        {studioFacilityTypes.map((k) => (
          <span key={k}
            className="px-2.5 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase bg-white/[0.06] border border-white/15 text-white/70"
            data-testid={`onboard-studio-recap-facility-${k}`}>
            {k.replace(/_/g, " ")}
          </span>
        ))}
        {sectionAffiliations.length > 0 && (
          <span className="px-2.5 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase bg-indigo-500/12 border border-indigo-500/30 text-indigo-200"
            data-testid="onboard-studio-recap-aff-count">
            {sectionAffiliations.length} affiliation{sectionAffiliations.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <Stepper steps={STEPS} step={step} />

      {step === 0 && (
        <Card title="Studio Details" testid="step-studio-info">
          <Field label="Legal Name *" tid="studio-legal">
            <Input value={legal} onChange={(e) => setLegal(e.target.value)}
              placeholder="e.g. Yash Raj Studios Pvt Ltd"
              className="h-11 bg-black/40 border-white/15" data-testid="studio-legal-input" />
          </Field>
          <Field label="Brand Name" tid="studio-brand">
            <Input value={brand} onChange={(e) => setBrand(e.target.value)}
              placeholder="e.g. YRF Studios"
              className="h-11 bg-black/40 border-white/15" data-testid="studio-brand-input" />
          </Field>
          <Field label="Registered Address" tid="studio-address">
            <Textarea value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="Building, Street, City, State, PIN"
              className="bg-black/40 border-white/15" data-testid="studio-address-input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="City *" tid="studio-city">
              <Input value={city} onChange={(e) => setCity(e.target.value)}
                placeholder="Mumbai · Hyderabad · Chennai"
                className="h-11 bg-black/40 border-white/15" data-testid="studio-city-input" />
            </Field>
            <Field label="State *" tid="studio-state">
              <Input value={state} onChange={(e) => setState(e.target.value)}
                placeholder="Maharashtra · Karnataka …"
                className="h-11 bg-black/40 border-white/15" data-testid="studio-state-input" />
            </Field>
          </div>
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/8 p-3">
            <div className="flex items-start gap-2">
              <MapPin size={14} className="text-emerald-300 shrink-0 mt-0.5" />
              <div className="text-[11px] text-white/75 leading-relaxed">
                <span className="text-emerald-200 font-semibold">Location on map (recommended).</span>{" "}
                Paste your Google Maps share URL — we&apos;ll extract the coordinates so
                crew can navigate in one tap. Open Google Maps → find your studio →
                <em> Share</em> → copy link.
              </div>
            </div>
            <Input value={mapsUrl} onChange={(e) => setMapsUrl(e.target.value)}
              placeholder="https://maps.app.goo.gl/… or https://www.google.com/maps/@19.12,72.87,17z/…"
              className="mt-2.5 h-10 bg-black/40 border-white/15 text-xs"
              data-testid="studio-maps-url-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="GSTIN" tid="studio-gstin">
              <Input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())}
                placeholder="27ABCDE1234F1Z5" maxLength={15}
                className="h-11 bg-black/40 border-white/15 font-mono uppercase"
                data-testid="studio-gstin-input" />
            </Field>
            <Field label="PAN" tid="studio-pan">
              <Input value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())}
                placeholder="ABCDE1234F" maxLength={10}
                className="h-11 bg-black/40 border-white/15 font-mono uppercase"
                data-testid="studio-pan-input" />
            </Field>
          </div>
          <NavRow onNext={next} disabledNext={!legal || !city.trim() || !state.trim()} />
        </Card>
      )}

      {step === 1 && (
        <Card title="Owner Account" testid="step-studio-owner">
          <Field label="Full Name *" tid="studio-owner-name">
            <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
              className="h-11 bg-black/40 border-white/15" data-testid="studio-owner-name-input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email *" tid="studio-owner-email">
              <Input type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
                className="h-11 bg-black/40 border-white/15" data-testid="studio-owner-email-input" />
            </Field>
            <Field label="Phone" tid="studio-owner-phone">
              <Input value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)}
                placeholder="+91 …"
                className="h-11 bg-black/40 border-white/15" data-testid="studio-owner-phone-input" />
            </Field>
          </div>
          <Field label="Password * (min 6 characters)" tid="studio-owner-pwd">
            <Input type="password" value={ownerPwd} onChange={(e) => setOwnerPwd(e.target.value)}
              className="h-11 bg-black/40 border-white/15" data-testid="studio-owner-pwd-input" />
          </Field>
          <NavRow onBack={back} onSubmit={submit} submitting={submitting}
            submitLabel="Register Studio"
            disabledNext={!ownerName || !ownerEmail || ownerPwd.length < 6} />
        </Card>
      )}

      {step === 2 && (
        <Card testid="step-studio-success">
          <div className="text-center py-6" data-testid="studio-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <ShieldCheck size={26} className="text-emerald-400" />
            </div>
            <h2 className="font-display text-2xl uppercase tracking-tight mt-5">Studio Registered</h2>
            <p className="mt-3 text-sm text-white/70 max-w-md mx-auto">
              Your studio is now visible in the public directory with a map pin. Open
              the dashboard to add staff roles, rooms, and start accepting bookings.
            </p>
            <Button onClick={() => nav("/dashboard")}
              className="mt-7 btn-themed h-11 px-7 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center"
              data-testid="studio-go-dashboard">
              Open Dashboard <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
